import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHero } from "./Services";

const SERVICE_TYPES = ["PC Repair", "PC Health Check", "Website Project", "Website Maintenance", "Automation Project", "Printer Support", "Console Support", "Phone/Tablet Support", "Remote Assistance", "Digital Download Help", "General Consultation"];
const URGENCY = ["Low", "Normal", "High", "Emergency"];

export default function RequestService() {
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", businessName: "", serviceType: "PC Repair", description: "", preferredTime1: "", urgency: "Normal", preferredContact: "email", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to continue."); return; }
    setLoading(true);
    try {
      const res = await api.post("/service-requests", form);
      setDone(res.data.requestNumber);
      toast.success("Request submitted!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div>
      <PageHero label="Request Service" title="Request received" />
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <CheckCircle2 className="h-16 w-16 text-electric mx-auto" />
        <h2 className="font-heading text-2xl font-bold text-navy mt-4">Thank you!</h2>
        <p className="mt-2 text-slate-600">Your request number is <span className="font-mono font-semibold text-navy">{done}</span>. Our team will review it and be in touch soon.</p>
      </div>
    </div>
  );

  const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";
  return (
    <div>
      <PageHero label="Request Service" title="Tell us what you need" subtitle="Fill out the form and we'll get right back to you. No obligation." />
      <form onSubmit={submit} data-testid="service-request-form" className="max-w-2xl mx-auto px-4 sm:px-6 py-14 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name *"><input required className={input} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} data-testid="req-name" /></Field>
          <Field label="Email *"><input required type="email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="req-email" /></Field>
          <Field label="Phone"><input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="req-phone" /></Field>
          <Field label="Business name"><input className={input} value={form.businessName} onChange={(e) => set("businessName", e.target.value)} data-testid="req-business" /></Field>
          <Field label="Service type *"><select className={input} value={form.serviceType} onChange={(e) => set("serviceType", e.target.value)} data-testid="req-service-type">{SERVICE_TYPES.map(s => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Urgency"><select className={input} value={form.urgency} onChange={(e) => set("urgency", e.target.value)} data-testid="req-urgency">{URGENCY.map(u => <option key={u}>{u}</option>)}</select></Field>
        </div>
        <Field label="Describe the issue or project *"><textarea required rows={5} className={`${input} h-auto py-2`} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="req-description" /></Field>
        <Field label="Preferred date/time"><input className={input} placeholder="e.g. Weekday mornings" value={form.preferredTime1} onChange={(e) => set("preferredTime1", e.target.value)} data-testid="req-time" /></Field>
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" data-testid="req-consent" />
          I consent to being contacted by Thomason Digital Works about my request.
        </label>
        <p className="text-xs text-slate-500">We never ask for passwords, card numbers, or banking details. Emergency service is not guaranteed and remains subject to confirmation.</p>
        <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="req-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}</Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-sm font-medium text-navy mb-1.5">{label}</span>{children}</label>;
}
